import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import {
  disableNetwork,
  doc,
  enableNetwork,
  getDocFromCache,
  getDocFromServer,
  setDoc,
} from "firebase/firestore";
import {
  FUNCTIONS_HOST_PROBE_URL,
  IMAGE_HOST_PROBE_URL,
  defaultHostRequest,
  formatDomImages,
  probeHost,
  summarizeDomImages,
  summarizeDomState,
  summarizeDomCensus,
} from "../../lib/firebase/resumeProbes";
import { db } from "../../lib/firebase/config";
import { isNativeApp } from "../../lib/platform/native";
import {
  claimRecoverySlot,
  claimScreenRecoverySlot,
  clearRecoveryCount,
  cycleFirestoreNetwork,
  probeFirestoreQueue,
  verifyFirestoreDelivery,
  type FirestoreQueueVerdict,
} from "../../lib/firebase/firestoreHealth";
import { reviveDeadFirestoreListeners } from "../../lib/firebase/resilientSnapshot";
import { bootMark } from "../../lib/boot/bootTrace";
import { flushResumeTrace, recordJsBeat, traceResume } from "../../lib/firebase/resumeTrace";
import {
  createResumeBurstMeter,
  type ResumeBurstReport,
  type ResumeSource,
} from "../../lib/firebase/resumeBurst";
import { captureException } from "../../lib/monitoring";
import { forceFullRepaint, nudgeCompositor, probePaintPulse } from "../../lib/platform/paintPulse";

/**
 * FirestoreResumeWatcher — o app volta do background e o Firestore não volta.
 *
 * Relato de device (27/08): *"desliguei a tela, voltei, saí da aba do
 * aplicativo e tentei usar — o aplicativo travou. A tela até tem informações
 * ainda mas não sai desse travamento."* Só matar e reabrir o app resolve.
 *
 * A causa provável está atacada em `firestoreCache.ts` (o modo multi-aba do
 * cache local, que escreve no IndexedDB justamente quando o WebKit congela o
 * processo). Este componente é a rede embaixo: mesmo com a causa consertada,
 * uma transação de IndexedDB pode morrer na suspensão, e a `AsyncQueue` do
 * Firestore é serial — travou uma, travaram todas, para sempre. Hoje o app não
 * tem caminho de volta nenhum; passa a ter.
 *
 * ⚠️ **2ª camada (BUG-179, 29/08):** o sintoma sobreviveu à 1.7 porque existe
 * um segundo modo de morte que a sonda de cache não vê — o TRANSPORTE morre em
 * silêncio com a fila viva (detalhe medido em `firestoreHealth.ts`). Por isso,
 * no nativo, o veredito `alive` é seguido de um ciclo
 * `disableNetwork`/`enableNetwork`: o mesmo `restartNetwork` que o SDK
 * executaria se o WKWebView disparasse o evento `online` que nunca vem.
 *
 * ⚠️ **3ª camada (BUG-196, 01/09):** o sintoma sobreviveu à 1.8 porque o ciclo
 * ASSUMIA a cura — `enableNetwork` resolve antes de o stream reconectar, e
 * quando a falha está abaixo dos streams (pool de conexões do WKWebView morto;
 * nem `<img>` carrega nesse estado) o ciclo "dá certo" sem curar nada. Agora a
 * entrega é VERIFICADA com uma leitura de servidor (desfecho saudável =
 * `permission-denied`, custo zero); transporte que segue morto escala pro
 * reload, que é a saída que comprovadamente cura. E cada passo deixa rastro
 * durável (`resumeTrace`) — o próximo relato de device chega com dado.
 *
 * ## Três decisões que não são estilo
 *
 * 1. **A sonda é uma leitura de CACHE de um documento que não existe.** Custo
 *    zero (não vai ao servidor, não conta leitura no Firestore) e, ainda
 *    assim, exercita exatamente o recurso que trava — a fila e o IndexedDB por
 *    trás dela. O desfecho saudável é uma REJEIÇÃO (`unavailable`), e é por
 *    isso que quem decide o veredito é `classifyProbeSettlement`, não o fato
 *    de ter dado erro.
 *
 * 2. **Espera assentar antes de sondar.** Na volta do background o IndexedDB
 *    pode levar um instante para acordar; sondar no primeiro frame
 *    transformaria lentidão normal em reload.
 *
 * 3. **`visibilitychange` E `appStateChange`.** O primeiro cobre web e PWA (que
 *    rodam no mesmo WebKit e têm a mesma exposição); o segundo é o sinal
 *    confiável no app nativo. Os dois caem no mesmo agendador, que colapsa a
 *    dupla chamada de uma retomada só.
 *
 * ⚠️ Recarregar é destrutivo para o que só existe em memória (um rascunho não
 * enviado). É o custo aceito: o estado alternativo é um app que não responde a
 * nada até ser morto — e a sessão de Foco em curso é recuperada do servidor no
 * boot pelo `FocusSessionProvider`.
 */

/** Documento inexistente e barato. Nunca é criado; a leitura é só de cache. */
const PROBE_PATH = "__lura_health__/probe";

/** Quanto esperar depois da retomada antes de sondar. */
const RESUME_SETTLE_MS = 1_200;

/** Piso entre duas sondagens, para troca rápida de app não virar rajada. */
const MIN_PROBE_INTERVAL_MS = 10_000;

/**
 * Entrega o relato de rajada (BUG-243): uma linha no diário local, para o
 * print, e UM evento no Cloud Logging pelo `captureException` — que carrega
 * `fingerprint`, então não dispara o alarme de erros das functions, e passa
 * pelo teto diário do `logClientError`. Mensagem fixa: o fingerprint (e o
 * dedupe de 60 s do monitoring) agrupa todos os aparelhos num evento só.
 */
function reportResumeBurst(report: ResumeBurstReport): void {
  traceResume(
    "burst",
    `${report.visible}v/${report.appActive}a em ${report.windowSec}s · escondido min ${report.hiddenMsMin ?? "-"}ms`,
  );
  captureException(new Error("resume_burst"), {
    ...report,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    native: isNativeApp(),
  });
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Modo privado / storage bloqueado: sem contador durável, a recuperação
    // se recusa a rodar (ver `claimRecoverySlot`).
    return null;
  }
}

export function FirestoreResumeWatcher() {
  useEffect(() => {
    // O rastro da carga ANTERIOR conta a história do reload que acabou de
    // acontecer — vai pro painel de boot e, se houve anormalidade, pro Cloud
    // Logging. Idempotente por `flushed`; StrictMode chamando duas vezes só
    // repete linhas de painel.
    flushResumeTrace();

    // Batimento do JS (7ª fatia, 2ª metade): DEPOIS do flush, que lê o
    // carimbo da carga anterior. Um `setItem` por segundo, só visível.
    // O espelho `__luraLastBeat` bate SEMPRE (é o que o ping nativo lê para
    // saber se os timers rodam); o carimbo durável só com a página visível,
    // para o `js-last-beat` do boot ser o último segundo VISÍVEL.
    const beat = setInterval(() => {
      if (document.visibilityState === "visible") {
        recordJsBeat();
      } else {
        (window as unknown as Record<string, unknown>).__luraLastBeat = Date.now();
      }
    }, 1_000);

    // A versão do iOS no diário: a família BUG-177..197 pode ser regressão de
    // versão do WebKit, e até aqui nenhum relato dizia QUAL iOS o aparelho
    // roda — correlacionar episódios com a versão é de graça a partir daqui.
    bootMark("device", "info", navigator.userAgent);

    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let lastProbeAt = 0;

    const runProbe = async () => {
      if (disposed || inFlight) return;
      inFlight = true;
      lastProbeAt = Date.now();
      // "wake" é o marco de que o watcher RODOU nesta retomada. Sem ele, um
      // episódio em que a pessoa mata o app no meio do pipeline fica
      // indistinguível de "o watcher nunca acordou" — que foi exatamente a
      // ambiguidade do QA do build 83 (BUG-196).
      traceResume("wake");
      try {
        await probeAndHeal();
      } finally {
        // `inFlight` cobre a sonda E o ciclo de rede: baixá-lo antes do ciclo
        // deixaria uma retomada rápida sobrepor um segundo ciclo ao primeiro.
        inFlight = false;
      }
    };

    // ── Ressurreição de listeners esgotados (continuação BUG-197) ──────────
    // Só roda nos desfechos em que o transporte está PROVADO são (verify-ok
    // no nativo; fila viva na web, onde ela é a última palavra) — re-assinar
    // sobre transporte morto queimaria o orçamento novo de retentativas à toa.
    // É o mecanismo que fecha "todas as sondas verdes e a tela sem carregar":
    // listener que esgotou as 4 retentativas ficava morto até matar o app.
    const reviveListeners = () => {
      const revived = reviveDeadFirestoreListeners();
      if (revived > 0) traceResume("listeners-revived", String(revived));
    };

    // ── Sondas dos canais que verify-ok NÃO cobre (BUG-197, 4ª fatia) ─────
    // Escrita (custo zero: as rules negam o doc da sonda e escrita negada não
    // é cobrada — `permission-denied` prova a ida e volta, como no verify de
    // leitura) · host das capas · host das callables · foto das <img> do DOM.
    // Só a escrita morta ESCALA (é o canal de "para de ter ações"); os
    // outros são diagnóstico, porque pool HTTP/2 morto no NetworkProcess do
    // WebKit não se cura com reload — só matando o app — e saber ISSO é o
    // que decide a próxima fatia.
    const runExtendedProbes = async (storage: Storage | null) => {
      const write = await verifyFirestoreDelivery(
        () => setDoc(doc(db, PROBE_PATH), { t: Date.now() }),
        () => navigator.onLine !== false,
      );
      if (disposed) return;
      if (write === "dead") {
        if (!claimRecoverySlot(storage)) {
          traceResume("no-slot", "verify-write-dead");
        } else {
          traceResume("verify-write-dead");
          traceResume("reload");
          console.warn("[Lura] stream de escrita do Firestore morto na retomada — recarregando.");
          window.location.reload();
          return;
        }
      } else {
        traceResume(write === "delivering" ? "verify-write-ok" : "verify-write-offline");
      }

      const [img, fn] = await Promise.all([
        probeHost(defaultHostRequest(IMAGE_HOST_PROBE_URL)),
        probeHost(defaultHostRequest(FUNCTIONS_HOST_PROBE_URL)),
      ]);
      if (disposed) return;
      traceResume(`img-host-${img}`);
      traceResume(`fn-host-${fn}`);
      traceResume("dom-imgs", formatDomImages(summarizeDomImages(document)));
    };

    const probeAndHeal = async () => {
      const storage0 = sessionStorageOrNull();

      // ── Pulso de PINTURA primeiro (BUG-197, só nativo) ───────────────────
      // O diário do build 84 provou o transporte VIVO nos episódios de tela
      // congelada (`wake → verify-ok`) — o modo de morte real é a pintura, e
      // toque/scroll não curam (relato de device). Sem frame novo no teto, a
      // única saída conhecida é a mesma da fila morta: reload com vaga. Vai
      // ANTES da sonda de fila de propósito: com a tela congelada, ciclar e
      // verificar transporte é gastar 10s para chegar ao mesmo reload.
      if (isNativeApp()) {
        // A foto do DOM ANTES de qualquer nudge (5ª fatia): separa "creme do
        // DOM" (tema perdido, root vazio, nav sumida — bug nosso) de "creme
        // de fora do documento". É a linha que faltava em todo episódio.
        traceResume("dom-state", summarizeDomState(document, window));
        // Força um commit da árvore de layers ANTES de medir (4ª fatia): se
        // o WKWebView está escondendo o conteúdo à espera de um commit que
        // não vem, é este o commit — e o pulso abaixo mede o estado DEPOIS.
        nudgeCompositor();
        // E obriga TODA layer a repintar (5ª fatia): backing store purgado no
        // background só volta com repaint, e commit estrutural não repinta.
        forceFullRepaint();
        const pulse = await probePaintPulse();
        if (disposed) return;
        if (pulse === "frozen") {
          if (!claimRecoverySlot(storage0)) {
            traceResume("no-slot", "paint-frozen");
            console.error(
              "[Lura] tela congelada na retomada e sem vaga de recuperação. " +
                "Feche e reabra o app.",
            );
            return;
          }
          traceResume("paint-frozen");
          traceResume("reload");
          console.warn("[Lura] tela congelada na retomada — recarregando.");
          window.location.reload();
          return;
        }
        traceResume(pulse === "painting" ? "paint-ok" : "paint-hidden");
      }

      let verdict: FirestoreQueueVerdict;
      try {
        verdict = await probeFirestoreQueue(() => getDocFromCache(doc(db, PROBE_PATH)));
      } catch (err) {
        // A sonda em si não pode derrubar nada. Erro inesperado aqui não é
        // prova de fila morta — recarregar por causa dele seria pior.
        console.warn("[Lura] sonda do Firestore falhou de forma inesperada.", err);
        return;
      }
      if (disposed) return;

      const storage = sessionStorageOrNull();

      if (verdict === "alive") {
        // ⚠️ As vagas de recuperação NÃO são devolvidas aqui. "Fila viva" já
        // não é prova de saúde (BUG-179/196); devolver antes da verificação
        // rearmaria o teto a cada retomada e transformaria "transporte morto
        // que o ciclo não cura" em loop de reload — exatamente o que o teto
        // existe para impedir. Quem devolve é a prova de entrega, abaixo.

        // Fila viva NÃO significa transporte vivo (BUG-179): a suspensão do
        // iOS mata o Watch stream em silêncio, o SDK segue se achando online e
        // TODO dado de servidor — onSnapshot e one-shot — para de chegar. O
        // empurrão que o SDK espera (evento `online`) nunca vem no WKWebView,
        // então quem dá é o app: o ciclo derruba e reabre os streams, e o
        // próprio SDK re-envia todos os listens registrados. Só no nativo — na
        // web o evento `online` funciona, e a doutrina do BUG-177 é não tocá-la:
        // lá a fila viva segue sendo a última palavra, e devolve as vagas.
        if (!isNativeApp()) {
          clearRecoveryCount(storage);
          reviveListeners();
          return;
        }

        const outcome = await cycleFirestoreNetwork(async () => {
          await disableNetwork(db);
          await enableNetwork(db);
        });
        if (disposed) return;

        if (outcome === "hung") {
          // As duas chamadas são locais à fila; não assentar é a fila presa —
          // o estado da sonda, alcançado por outra porta. Mesma saída dela.
          if (!claimRecoverySlot(storage)) {
            traceResume("no-slot", "cycle-hung");
            console.error(
              "[Lura] ciclo de rede do Firestore pendurou e sem vaga de recuperação. " +
                "Feche e reabra o app.",
            );
            return;
          }
          traceResume("cycle-hung");
          traceResume("reload");
          console.warn("[Lura] ciclo de rede do Firestore pendurou — recarregando.");
          window.location.reload();
          return;
        }

        if (outcome === "failed") {
          // Rejeição não é prova de fila morta; recarregar por ela seria
          // falso positivo. Fica o registro para o painel de boot/console.
          traceResume("cycle-failed");
          console.warn("[Lura] ciclo de rede do Firestore falhou; seguindo sem reload.");
          return;
        }

        // "restarted" é ASPIRACIONAL (BUG-196): `enableNetwork` resolve quando
        // a fila processa a ordem, não quando o stream reconecta — o start é
        // disparado, nunca aguardado (medido no SDK). O 1.8 embarcou o ciclo e
        // o sintoma continuou porque este era o único caminho que terminava
        // sem cura e sem reload. Daqui em diante o ciclo é VERIFICADO: uma
        // leitura que exige o servidor, cujo desfecho saudável é o próprio
        // servidor negar (`permission-denied` = ida e volta provada, custo
        // zero). Só o modo avião pula — reload sobre rede que não existe é
        // castigo, não cura.
        if (navigator.onLine === false) {
          traceResume("verify-skip", "offline");
          return;
        }

        const delivery = await verifyFirestoreDelivery(
          () => getDocFromServer(doc(db, PROBE_PATH)),
          () => navigator.onLine !== false,
        );
        if (disposed) return;

        if (delivery === "delivering") {
          // Ida e volta provada — só AQUI a saúde está demonstrada e as vagas
          // de recuperação voltam ao teto.
          clearRecoveryCount(storage);
          traceResume("verify-ok");
          reviveListeners();
          // O build 87 provou que verify-ok NÃO basta como veredito de saúde:
          // ele mede o stream de LEITURA. Os canais que restam — escrita,
          // capas, callables — são sondados aqui, depois, fora do caminho
          // crítico (o app já está utilizável enquanto rodam).
          await runExtendedProbes(storage);
          return;
        }

        if (delivery === "offline") {
          // Sem rede não há o que provar nem o que curar; as vagas ficam como
          // estão até uma retomada com rede fechar o veredito.
          traceResume("verify-offline");
          return;
        }

        if (!claimRecoverySlot(storage)) {
          traceResume("no-slot", "verify-dead");
          console.error(
            "[Lura] transporte do Firestore segue morto e sem vaga de recuperação. " +
              "Feche e reabra o app.",
          );
          return;
        }
        traceResume("verify-dead");
        traceResume("reload");
        console.warn(
          "[Lura] transporte do Firestore não entregou depois do ciclo — recarregando.",
        );
        window.location.reload();
        return;
      }

      if (!claimRecoverySlot(storage)) {
        // Teto estourado (ou sem storage durável): parar aqui é a decisão.
        // Ver o docblock de `claimRecoverySlot` — loop de boot é pior.
        traceResume("no-slot", "probe-dead");
        console.error(
          "[Lura] Firestore travado na retomada e sem vaga de recuperação. " +
            "Feche e reabra o app.",
        );
        return;
      }

      traceResume("probe-dead");
      traceResume("reload");
      console.warn("[Lura] Firestore travado na retomada — recarregando para recuperar.");
      window.location.reload();
    };

    const schedule = () => {
      if (disposed) return;
      if (document.visibilityState !== "visible") return;
      if (timer !== undefined) return; // retomada já agendada

      // ⚠️ O piso anti-rajada ADIA, nunca descarta (QA do build 83, BUG-196).
      // A versão anterior retornava sem agendar quando a retomada caía a menos
      // de 10s da sonda anterior — e o roteiro real do fundador é justamente
      // sair-e-voltar rápido: a segunda volta (a que pega o transporte morto)
      // ficava SEM sonda nenhuma, para sempre, porque não há um terceiro
      // evento para reagendar. Adiar preserva o controle de rajada e garante
      // que TODA retomada acaba sondada.
      const wait = Math.max(
        RESUME_SETTLE_MS,
        lastProbeAt + MIN_PROBE_INTERVAL_MS - Date.now(),
      );
      timer = setTimeout(() => {
        timer = undefined;
        void runProbe();
      }, wait);
    };

    // ── BUG-243: rajada de retomadas vira UM relato ─────────────────────────
    // Observação, nunca política: nada aqui muda a sonda. O medidor decide
    // quando é rajada (`lib/firebase/resumeBurst.ts`); aqui só se alimenta e
    // se entrega — com o UA, que é o que o relato de 14/09 não tinha.
    const burst = createResumeBurstMeter();
    const noteResume = (source: ResumeSource) => {
      const report = burst.resumed(source, Date.now());
      if (report) reportResumeBurst(report);
    };

    const onVisibility = () => {
      // ── 7ª fatia: os marcadores SÍNCRONOS ────────────────────────────────
      // `wake` nasce 1,2s depois do evento, num setTimeout — uma thread de JS
      // presa não executa timer nenhum, e o episódio congelado do build 89
      // não deixou UMA linha (os três episódios do diário eram os sãos). O
      // que se escreve aqui roda no próprio handler, antes de qualquer
      // await: `hidden` leva a rota e o censo da página que foi pro fundo
      // (é a única linha que pode nomear a Home congelada), `visible` prova
      // que o evento de volta chegou ao JS. O `flushResumeTrace` do boot
      // seguinte lê o que faltou depois do último `hidden`.
      if (document.visibilityState === "hidden") {
        traceResume("hidden", summarizeDomCensus(document));
        burst.hidden(Date.now());
        return;
      }
      if (document.visibilityState === "visible") {
        traceResume("visible");
        noteResume("visibility");
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── Gancho da sentinela NATIVA de pintura (BUG-197, 3ª fatia) ──────────
    // O AppDelegate fotografa o que está NA TELA (drawHierarchy) na retomada e
    // reporta por aqui — porque rAF mente para esta camada (o build 86 provou
    // paint-ok + verify-ok + zero listener morto com a tela quebrada). O
    // reload passa pelo MESMO orçamento de vagas dos outros caminhos: sonda
    // nativa mentindo em tela sã não pode virar loop de reload.
    const nativePaintHook = (verdict: string) => {
      // ── 5ª fatia: a amostra da JANELA e a geometria ──────────────────────
      // `geom:<detalhe>` é a hierarquia nativa (janelas, o que está ACIMA da
      // WebView, frame, alpha, scroll) — só diário. `screen-*` é o que o
      // vidro compõe para a janela inteira, medido ao lado do takeSnapshot
      // (que a fonte do WebKit prova ser o compositor da layer da WebView):
      // os dois concordando = o creme vem de fora do app; discordando = o
      // creme está entre a layer e a janela. `screen-dead` = uniforme DEPOIS
      // do nudge de CA — recarrega, com orçamento próprio que nunca rearma.
      if (verdict.startsWith("geom:")) {
        traceResume("native-geom", verdict.slice(5));
        return;
      }
      // ── 7ª fatia: o ring NATIVO replicado no boot seguinte ───────────────
      // O AppDelegate guarda em UserDefaults o que aconteceu do lado de fora
      // do JS (`active`, `background`, o ping de vida e os vereditos das
      // sondas) e despeja aqui quando este hook existe. É o que faz `active`
      // sem `visible` no JS virar dado, e é a ÚNICA forma de o `js-hung`
      // chegar ao diário — a thread que não respondeu ao ping não grava nada.
      if (verdict.startsWith("prev:")) {
        for (const line of verdict.slice(5).split("\n")) {
          if (!line) continue;
          // `webview-preventive` é a recriação decidida na retomada (Home e
          // Perfil próprio, decisão do fundador em 02/09): acontece a toda
          // volta do fundo, então é INFO, não anormal — telemetria por ela
          // seria uma exceção por retomada.
          const step = line.includes(" webview-recreated")
            ? "native-webview-recreated"
            : line.includes(" webview-preventive")
              ? "native-webview-preventive"
              : line.includes(" js-hung")
                ? "native-js-hung"
                : line.includes(" js-stale")
                  ? "native-js-stale"
                  : "native-prev";
          traceResume(step, line);
        }
        return;
      }
      // ── 6ª fatia: a janela por FAIXAS ────────────────────────────────────
      // `screen-ok` mede o espalhamento da janela INTEIRA — e a janela inteira
      // tem a status bar (relógio, bateria) e a bottom nav (fixa, layer
      // própria, repintada em separado). Com o MIOLO creme e a nav pintada, o
      // espalhamento é alto e a amostra diz "ok": é exatamente o estado que o
      // build 89 devolveu. `bands:top=<t> mid=<m> bot=<b>` separa as três —
      // só diário; `mid` baixo com `bot` alto é "conteúdo morto, casca viva".
      if (verdict.startsWith("bands:")) {
        traceResume("native-screen-bands", verdict.slice(6));
        return;
      }
      if (verdict.startsWith("screen-")) {
        const step = `native-${verdict}`;
        if (verdict !== "screen-dead") {
          traceResume(step);
          return;
        }
        if (!claimScreenRecoverySlot(sessionStorageOrNull())) {
          traceResume("no-slot", step);
          return;
        }
        traceResume(step);
        traceResume("reload");
        window.location.reload();
        return;
      }
      if (verdict === "ok" || verdict === "nosample") {
        // A sentinela reporta SEMPRE (4ª fatia): "ok" = a foto mostrou
        // conteúdo; "nosample" = o takeSnapshot não devolveu foto. Info, não
        // anormal — mas sem esta linha o diário do 87 foi ilegível.
        traceResume(`native-paint-${verdict}`);
        return;
      }
      if (verdict === "suspect") {
        // A CALIBRAÇÃO da sentinela: a amostra deu uniforme e o nativo vai
        // tentar o re-attach. Se esta linha aparecer em retomada SAUDÁVEL, a
        // sonda mente nesta versão do iOS — e é o diário que decide isso, não
        // outro build às cegas. Info, não anormal.
        traceResume("native-paint-suspect");
        return;
      }
      if (verdict === "recovered") {
        // O re-attach nativo curou — houve congelamento real, e esta linha é
        // a manchete que decide a calibração da sentinela.
        traceResume("native-paint-recovered");
        return;
      }
      // "dead" = uniforme mesmo depois do re-attach · "hung" = o takeSnapshot
      // nunca voltou (4ª fatia — a assinatura do vídeo do build 87). Os dois
      // terminam no mesmo lugar: reload com vaga do orçamento.
      if (verdict !== "dead" && verdict !== "hung") return;
      const step = `native-paint-${verdict}`;
      const storage = sessionStorageOrNull();
      if (!claimRecoverySlot(storage)) {
        traceResume("no-slot", step);
        return;
      }
      traceResume(step);
      traceResume("reload");
      window.location.reload();
    };
    if (isNativeApp()) {
      (window as unknown as Record<string, unknown>).__luraNativePaint = nativePaintHook;
    }

    // O plugin devolve um handle por promise; o cleanup pode rodar antes dela
    // assentar (StrictMode monta-desmonta-monta). Mesmo padrão do
    // `NativeBackButtonBridge`, incluindo o unsubscribe plano — o Proxy do
    // `registerPlugin` é o thenable falso do BUG-154.
    let removeAppListener: (() => void) | null = null;
    if (isNativeApp()) {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) return;
        noteResume("app");
        schedule();
      })
        .then((handle) => {
          if (disposed) void handle.remove();
          else removeAppListener = () => void handle.remove();
        })
        .catch((err) => {
          // Sobra o `visibilitychange`, que o WKWebView também dispara.
          console.warn("[Lura] appStateChange indisponível.", err);
        });
    }

    return () => {
      disposed = true;
      if (timer !== undefined) clearTimeout(timer);
      clearInterval(beat);
      document.removeEventListener("visibilitychange", onVisibility);
      const w = window as unknown as Record<string, unknown>;
      if (w.__luraNativePaint === nativePaintHook) delete w.__luraNativePaint;
      removeAppListener?.();
    };
  }, []);

  return null;
}
